    import React, { useState, useEffect } from 'react';
    import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
    import { ProjectRelativeLink } from '@/components/navigation';
    import { addProjectMember, getProject, checkInviteExternalEmail } from '@/services/projects';
    import { LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
    import { ValidationForm, ValidationSummary } from '@/components/forms/validation';
    import ProjectPermissionsEditor from '@/seller/components/project/UserManagement/ProjectPermissionsEditor';
    import { Button } from '@/components/Buttons';
    import { useAsyncState, useValidationManager, useDebounce } from '@/utils/hooks';
    import { ProjectMemberPermissions, Profile, EmailValidity } from '@/types';
    import { connect } from 'react-redux';
    import { IState } from '@/state/IState';
    import { ProfileAutocomplete } from '@/components/Autocomplete';
    import ProjectInviteExternalEmailInput from './ProjectInviteExternalEmailInput';
    import ProjectInviteWrapper from './ProjectInviteWrapper';
    import { faUserPlus } from '@fortawesome/pro-light-svg-icons';
    import theme from './ProjectInviteExternal.module.scss';

    const mapStateToProps = (state: IState) => ({
        projectId: state.global.activeProject!.id,
        profileId: state.global.activeProfile!.id,
    });

    type OwnProps = ReturnType<typeof mapStateToProps> & {};

    const ProjectInviteInternal: React.FC<OwnProps> = ({ projectId, profileId }) => {
        const [email, setEmail] = useState<string>('');
        const [selectedProfile, setSelectedProfile] = useState<Profile | null>(null);
        const [permissions, setPermissions] = useState<ProjectMemberPermissions>({
            categoryPermissions: [],
            projectPermissions: [],
            allowedProjectFeeSetIds: [],
            isAdministrator: false,
        });
        const [saving, setSaving] = useState(false);
        const [success, setSuccess] = useState(false);
        const [project] = useAsyncState(() => getProject(projectId), [projectId], null);
        const [emailValidity, setEmailValidity] = useState<EmailValidity>(EmailValidity.NotChecked);

        const emailDebounced = useDebounce(email, 250);

        useEffect(() => {
            if (selectedProfile) {
                setEmailValidity(EmailValidity.Checking);
            }
        }, [email, selectedProfile]);

        useEffect(() => {
            async function checkEmail() {
                if (!selectedProfile) {
                    setEmailValidity(EmailValidity.NotChecked);
                    return;
                }

                if (emailDebounced.length === 0) {
                    setEmailValidity(EmailValidity.NotChecked);
                    return;
                }

                try {
                    setEmailValidity(EmailValidity.Checking);
                    const validity = await checkInviteExternalEmail(projectId, emailDebounced, selectedProfile.id);
                    setEmailValidity(validity);
                } catch (e: any) {
                    console.error(e);
                }
            }

            checkEmail();
        }, [emailDebounced, selectedProfile, projectId]);

        const validationManager = useValidationManager([
            {
                isValid: (value) => !!selectedProfile,
                summaryMessage: ({ field }) => <Trans>{field} is required</Trans>,
                messageParameters: { field: <Trans>Company</Trans> },
            },
        ]);
        const handleFormSubmit = async (e) => {
            if (emailValidity === EmailValidity.NotChecked) {
                return;
            }

            setSaving(true);

            try {
                await addProjectMember({
                    ...permissions,
                    inviteeEmail: email,
                    inviteeProfileId: selectedProfile!.id,
                    inviterProfileId: profileId,
                    projectId,
                });

                setSuccess(true);
            } finally {
                setSaving(false);
            }
        };

        const handleReset = () => {
            setEmail('')
            setSelectedProfile(null)
            setPermissions({
                categoryPermissions: [],
                projectPermissions: [],
                allowedProjectFeeSetIds: [],
                isAdministrator: false,
            })
            setEmailValidity(EmailValidity.NotChecked)
            setSaving(false)
            setSuccess(false)
        }

        if (!project) {
            return <LoadingIndicator />;
        }

        if (success) {
            return (
                <ProjectInviteWrapper projectName={project.name}>
                    <h3>
                        <Trans>Your invitation has been sent to {email}.</Trans>
                    </h3>
                    <h6>
                        <ProjectRelativeLink className="link-unstyled" to={`/users`}>
                            <Button color="primary">
                                <Trans>Back to project permissions</Trans>
                            </Button>
                        </ProjectRelativeLink>
                        <Button
                            onClick={handleReset}
                            className={theme.inviteMore}
                            color="tertiary"
                            icon={faUserPlus}>
                            <Trans>Invite a user</Trans>
                        </Button>
                    </h6>
                </ProjectInviteWrapper>
            );
        }

        return (
            <ProjectInviteWrapper projectName={project.name}>
                <ValidationForm onSubmit={handleFormSubmit} validationManager={validationManager}>
                    <div className="form-group">
                        <label>
                            <Trans>Company</Trans>
                        </label>
                        <div>
                            <ProfileAutocomplete
                                className="form-control"
                                onChange={(member) => setSelectedProfile(member)}
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label>
                            <Trans>Email</Trans>
                        </label>
                        <ProjectInviteExternalEmailInput
                            required
                            className="form-control"
                            placeholder={t`Email`}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            emailValidity={emailValidity}
                        />
                        {emailValidity === EmailValidity.AlreadyExists && (
                            <div className={theme.error}>
                                <Trans>User with this email already invited.</Trans>
                            </div>
                        )}
                    </div>

                    <div className="form-group">
                        <ValidationSummary />
                    </div>

                    <hr />

                    <div className="form-group">
                        <ProjectPermissionsEditor
                            projectId={projectId}
                            value={permissions}
                            onChange={(value) => setPermissions(value)}
                        />
                    </div>

                    <div className="form-group">
                        <ValidationSummary />

                        <Button
                            type="submit"
                            color="primary"
                            loading={saving}
                            className="float-right"
                            disabled={emailValidity === EmailValidity.AlreadyExists}>
                            <Trans>Add user</Trans>
                        </Button>
                    </div>
                </ValidationForm>
            </ProjectInviteWrapper>
        );
    };

    export default connect(mapStateToProps)(ProjectInviteInternal);
